import { ReactNode, useState } from "react";

import {
  HelperArticle,
  HelperPage,
} from "@tudigo-monorepo/core-tudigo-api-models";
import { useUserBusiness } from "@tudigo-monorepo/tudigo-session";
import { createHelperArticleFeedback } from "@tudigo-monorepo/web-tudigo-api-client";
import {
  AvatarList,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from "@tudigo-monorepo/web-tudigo-components";

import { HelperArticleListPreview } from "../helper-articles/articles-preview/helper-article-list-preview";
import { HelperVoter } from "../helper-voter/helper-voter";

export interface HelperDrawerProps {
  className?: string;
  trigger?: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  articlesData?: HelperPage;
}

export enum ArticleStatus {
  Published = "published",
  Review = "in_review",
  Draft = "draft",
}

export function HelperDrawer({
  trigger,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  articlesData,
}: HelperDrawerProps) {
  const { userIdOrMe } = useUserBusiness();
  const [_isOpen, _setIsOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<HelperArticle | null>(
    null,
  );

  const isOpen = controlledIsOpen ?? _isOpen;
  const onOpenChange = controlledOnOpenChange || _setIsOpen;

  const isArticleDetailVisible = !!selectedArticle;

  const onVote = async (value: number) => {
    try {
      const currentUserId = userIdOrMe;

      if (!currentUserId) {
        throw new Error("User ID is undefined");
      }

      await createHelperArticleFeedback(selectedArticle?.id ?? "", {
        id: `helper-article-feedback-${Date.now()}`,
        value,
        helper_article: {
          id: selectedArticle?.id ?? "",
        },
        user: {
          id: currentUserId,
        },
      });

      // TODO: Add user feedback
      console.log("Feedback submitted successfully");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  const publishedArticles = articlesData?.articles.filter(
    (article) => article.status === ArticleStatus.Published,
  );

  // TODO: Remove this once we have the real data
  const avatars = [
    {
      id: "1",
      url: "https://placehold.co/64x64",
      alt: "Avatar 1",
    },
    {
      id: "2",
      url: "https://placehold.co/64x64",
      alt: "Avatar 2",
    },
    {
      id: "3",
      url: "https://placehold.co/64x64",
      alt: "Avatar 3",
    },
    {
      id: "4",
      url: "https://placehold.co/64x64",
      alt: "Avatar 4",
    },
    {
      id: "5",
      url: "https://placehold.co/64x64",
      alt: "Avatar 5",
    },
  ];

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="w-full lg:w-[480px]">
        <div className="flex h-full flex-col">
          <SheetHeader className="flex h-[155px] justify-center bg-neutral-200">
            {avatars.length > 0 && (
              <AvatarList
                avatars={avatars}
                maxDisplayed={5}
                avatarSize="5xl"
                className="ml-4"
              />
            )}
          </SheetHeader>
          <div className="flex-grow overflow-y-auto px-10 py-6">
            <HelperArticleListPreview
              articles={publishedArticles ?? []}
              url={articlesData?.uri ?? ""}
              selectedArticle={selectedArticle}
              onArticleClick={setSelectedArticle}
            />
          </div>
          {isArticleDetailVisible && (
            <SheetFooter className="justify-center border-t bg-neutral-200 p-0 text-center">
              {/* TODO: Add onVote and onRequestSupport callbacks when API is ready */}
              <HelperVoter onVote={onVote} onRequestSupport={() => {}} />
            </SheetFooter>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
